import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, AlertCircle, Loader2, Folder, ImageIcon, X, RotateCcw } from "lucide-react";
import { Progress } from "@/components/ui/progress";

const inputCls = "h-8 w-full rounded border border-zinc-200 bg-white px-2 text-sm outline-none transition-colors focus:border-zinc-900 disabled:bg-transparent disabled:border-transparent disabled:px-0";

const CoverThumb = ({ file, status }) => {
  const url = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => () => url && URL.revokeObjectURL(url), [url]);
  if (!url) return <div className="flex h-14 w-14 items-center justify-center rounded bg-zinc-100 text-zinc-300"><Folder size={20} /></div>;
  return (
    <div className="relative h-14 w-14 shrink-0">
      <img src={url} alt="" className="h-14 w-14 rounded object-cover" />
      {status === "done" && <CheckCircle2 size={16} className="absolute -right-1.5 -top-1.5 rounded-full bg-white text-emerald-600" />}
      {status === "uploading" && <Loader2 size={16} className="absolute -right-1.5 -top-1.5 animate-spin rounded-full bg-white text-zinc-500" />}
    </div>
  );
};

const RowStatus = ({ it }) => {
  if (it.status === "done") return <CheckCircle2 size={17} className="text-emerald-600" />;
  if (it.status === "error") return <AlertCircle size={17} className="text-red-600" />;
  if (it.status === "reading") return <Loader2 size={15} className="animate-spin text-zinc-400" />;
  if (it.status === "uploading") return <span className="font-mono text-[11px] text-zinc-400">{it.progress < 100 ? `${it.progress}%` : "saving"}</span>;
  return null;
};

const Row = ({ it, locked, onEdit, onRemove, onRetry }) => {
  const editable = !locked && (it.status === "ready" || it.status === "error");
  const f = it.fields;
  const field = (k, ph, cls = "") => (
    <input value={f[k] ?? ""} placeholder={ph} disabled={!editable} onChange={(e) => onEdit(it.key, { [k]: e.target.value })}
      data-testid={`staged-${k}-${it.key}`} className={`${inputCls} ${cls}`} />
  );
  return (
    <div className="grid grid-cols-[3rem_1fr_1fr_1fr_2rem] items-center gap-3 px-4 py-2" data-testid={`staged-row-${it.key}`}>
      {field("track_no", "#", "text-center font-mono")}
      {field("title", "Title")}
      {field("artist", "Artist")}
      {field("album", "Album")}
      <div className="flex justify-end">
        {editable && it.status === "ready" ? (
          <button onClick={() => onRemove(it.key)} data-testid={`staged-remove-${it.key}`} className="text-zinc-300 hover:text-red-600"><X size={15} /></button>
        ) : <RowStatus it={it} />}
      </div>
      {(it.status === "uploading" || it.status === "error" || it.status === "done" || (it.status === "ready" && it.resume != null)) && (
        <div className="col-span-5 -mt-1 pl-[3.75rem]">
          {it.status === "ready" && it.resume != null && <p className="flex items-center gap-1 text-[11px] text-sky-700" data-testid={`staged-resume-${it.key}`}><RotateCcw size={11} />Resumes — {it.resume}% was already uploaded before the page reloaded</p>}
          {it.status === "uploading" && <Progress value={it.progress} className="h-1" />}
          {it.status === "error" && (
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-xs text-red-600" data-testid={`staged-error-${it.key}`}>{it.error}</p>
              {!locked && <button onClick={() => onRetry(it)} data-testid={`staged-retry-${it.key}`} className="flex h-6 items-center gap-1 rounded-full border border-zinc-300 bg-white px-2.5 text-[11px] font-medium text-zinc-700 transition-colors hover:border-zinc-900 hover:text-zinc-900"><RotateCcw size={11} />Retry</button>}
            </div>
          )}
          {it.status === "done" && <p className="truncate font-mono text-[11px] text-zinc-500" data-testid={`staged-path-${it.key}`}>{it.result.path.startsWith("Resonance/") ? it.result.path : `OneDrive${it.result.path}`}</p>}
        </div>
      )}
    </div>
  );
};

export const StagedGroup = ({ dir, items, cover, coverStatus, locked, onEdit, onEditGroup, onRemove, onRetry, index }) => {
  const [album, setAlbum] = useState("");
  const [artist, setArtist] = useState("");
  const first = items[0]?.fields || {};
  const editable = !locked && items.some((i) => i.status === "ready");
  return (
    <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white" data-testid={`staged-group-${index}`}>
      <div className="flex flex-wrap items-center gap-4 border-b border-zinc-200 bg-zinc-50/70 px-4 py-3">
        <CoverThumb file={cover} status={coverStatus} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium">{dir || "Loose files"}</p>
          <p className="flex items-center gap-1.5 text-xs text-zinc-500">
            {items.length} song{items.length > 1 ? "s" : ""}
            {cover && <span className="flex items-center gap-1" data-testid={`staged-cover-${index}`}><ImageIcon size={11} />{cover.name} will be saved as the album cover</span>}
          </p>
        </div>
        {dir && editable && (
          <div className="flex flex-wrap items-center gap-2">
            <input value={album} placeholder={first.album || "Album for all"} onChange={(e) => setAlbum(e.target.value)}
              data-testid={`group-album-input-${index}`} className={`${inputCls} w-44`} />
            <input value={artist} placeholder={first.artist || "Artist for all"} onChange={(e) => setArtist(e.target.value)}
              data-testid={`group-artist-input-${index}`} className={`${inputCls} w-44`} />
            <button disabled={!album.trim() && !artist.trim()} data-testid={`group-apply-button-${index}`}
              onClick={() => { onEditGroup(dir, { ...(album.trim() && { album: album.trim() }), ...(artist.trim() && { artist: artist.trim(), album_artist: artist.trim() }) }); setAlbum(""); setArtist(""); }}
              className="h-8 rounded-full bg-zinc-900 px-4 text-xs text-white disabled:opacity-30">Apply to all</button>
          </div>
        )}
      </div>
      <div className="grid grid-cols-[3rem_1fr_1fr_1fr_2rem] gap-3 px-4 pt-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-zinc-400">
        <span className="text-center">#</span><span>Title</span><span>Artist</span><span>Album</span><span />
      </div>
      <div className="divide-y divide-zinc-100 pb-1">
        {items.map((it) => <Row key={it.key} it={it} locked={locked} onEdit={onEdit} onRemove={onRemove} onRetry={onRetry} />)}
      </div>
    </div>
  );
};
